import type { ReactNode } from 'react';
import { useI18n } from '../i18n';

export function SkeletonGrid({ count = 12 }: { count?: number }) {
  return (
    <div className="skeleton-grid">
      {Array.from({ length: count }, (_, i) => (
        <div className="skeleton" key={i} />
      ))}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--muted)' }}>
      <span className="spinner" />
      {label}
    </span>
  );
}

export function Banner({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'warn' | 'error' | 'ok';
  children: ReactNode;
}) {
  const icon = kind === 'error' ? '⛔' : kind === 'warn' ? '⚠️' : kind === 'ok' ? '✅' : 'ℹ️';
  return (
    <div className={`banner ${kind}`}>
      <span aria-hidden>{icon}</span>
      <div>{children}</div>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  const { t } = useI18n();
  const code = (error as { code?: string }).code;
  return (
    <Banner kind="error">
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('error.title')}</div>
      <div>{error.message}</div>
      {code === 'youpin_auth_expired' && <div style={{ marginTop: 6 }}>{t('error.credentialsExpired', { date: '' })}</div>}
      {code === 'youpin_not_logged_in' && <div style={{ marginTop: 6 }}>{t('error.notLoggedIn')}</div>}
      {code === 'youpin_handshake_rejected' && <div style={{ marginTop: 6 }}>{t('error.handshake')}</div>}
      {code === 'youpin_api_error' && <div style={{ marginTop: 6 }}>{t('error.apiError')}</div>}
      {onRetry && (
        <button className="btn ghost small" style={{ marginTop: 10 }} onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </Banner>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
